/* eslint-disable statura/no-floating-point-money --
 * Canonical HPSS Award MA000027 regulatory calculator validated
 * against FWC pay guide. Every $-arithmetic line is part of the
 * regulated calc.
 */

/**
 * Phase 5 §7 — Health Professionals and Support Services Award (MA000027) engine.
 *
 * Implements the per-shift pay calculator for HPSS workers — allied health
 * professionals (physiotherapists, occupational therapists, social workers,
 * dietitians, podiatrists) and support services staff covered by MA000027.
 *
 * Reads all rate / penalty / allowance values from the RateContext
 * (Phase 1 effective-dated rate version).
 *
 * Stream codes (clause refs verified Phase 1I.3 against FWC consolidated
 * MA000027 ppc 1 Apr 2026; see
 * `docs/operations/clause-verifications/2026-05-16-aged-care-legislation-hpss-classification-completeness.md`):
 *   - 'health_professional' (cl 17.2–17.5) — Health Professional L1 (PP1–PP6),
 *     L2 (PP1–PP4), L3 (PP1–PP5), L4 (PP1–PP4). 19 distinct (level, payPoint)
 *     combinations. Authority: FWC PR786565 ppc 1 Jul 2025.
 *   - 'support_services' (cl 16.2(a)) — Support Services Employee L1, L2, L3,
 *     L4, L5, L6, L7, L8 (PP1–PP3), L9 (PP1–PP3). 13 distinct (level, payPoint)
 *     combinations. Authority: FWC PR795400 (substituted cl 16.2) as corrected
 *     by PR797960, ppc 1 Apr 2026.
 *   - 'cooking_apprentice' (cl 16.4) — Year 1 / Year 2 / Year 3 / Year 4 of
 *     indenture at 55% / 65% / 80% / 95% of cl 16.2(a) Level 4 base rate.
 *     Apprentices receive the same penalty, overtime, shift loading and
 *     weekend rates as adult Support Services workers per cl 16.8(a).
 *     Authority: same as cl 16.2(a) (apprentice rates derived from L4).
 *     Phase 1I.4 (2026-05-16) — stream added to HPSS_STREAMS; cl 16.7
 *     adult-apprentice (≥21) floor overlay (greater-of 80% L1 vs year-X
 *     cl 16.4) is DEFERRED to post-Phase-1 workstreams (zero production
 *     adult cooking apprentices at the cl 16.7 deferral decision).
 *
 * Out-of-engine streams (seeded in DB; not yet accepted by HPSS_STREAMS —
 * see the "Known platform gaps" notes for dental assistant / pathology
 * collector cl 16.2(b)–(c)). Add to HPSS_STREAMS when a customer requires.
 *
 * Schedule references in the current MA000027 consolidation:
 *   - Schedule A — Classification Definitions (criteria for level assignment).
 *   - Schedule C — Summary of Hourly Rates (penalty/OT-loaded rate matrix).
 *   - Schedule B is "List of Common Health Professionals" and does NOT carry
 *     classification rates — do not cite Schedule B for rate authority.
 *
 * Allowance scope under MA000027 (verified Phase 1I.0 sweep B.5 + 1I.4):
 *   - First-aid allowance: NOT in MA000027. (Both MA000034 Nurses cl 22 and
 *     MA000018 Aged Care have one; HPSS does not.) The shared
 *     `hasFirstAidResponsibility` input field is deliberately ignored by the
 *     HPSS allowance loop — no FIRST_AID line is emitted.
 *   - Second meal allowance (`MEAL_OT_ADDITIONAL` cl 23.3(e)(ii)) IS seeded
 *     in DB at $14.98 but is not consumed by the engine yet — sweep B.5
 *     secondary gap; pending post-Phase-1 workstream.
 *
 * Casual loading is HYBRID per cl 11.4 + cl 26.1(b) + cl 26.3(b):
 *   - Ordinary hours: 25% loading applies (base × 1.25)
 *   - Saturday/Sunday: 175% of base (the 25% loading is explicitly EXCLUDED
 *     by cl 26.1(b))
 *   - Public holiday: 275% of base (loading IS applied, per Schedule C.1.7)
 *   - Shift loading: 140% of base (loading explicitly excluded by cl 26.3(b))
 *   - Overtime: rates 187.5% / 250% / 312.5% (loading IS embedded — calculated
 *     by adding 25% to the FT/PT OT base before applying multiplier)
 *
 * This is critically different from BOTH SCHADS (purely inclusive) AND
 * Nurses (purely additive cl 11.4). Penalty multipliers stored in the rate
 * version are the FINAL paid multipliers (not "extras"). The engine reads
 * them directly without adding the 25% loading on top.
 *
 * For weekday shift hours (afternoon/night), the engine subtracts the 25%
 * loading from the shift-loading band so it does not compound. Sat/Sun
 * already happens automatically because those days are separate bands that
 * don't go through the weekdayMult path.
 *
 * Known v1 limitations (same pattern as Nurses + Aged Care):
 *  - Per-segment afternoon/night shift detection (whole-shift refactor
 *    is a Phase 6 cleanup task).
 *  - OT-end-of-shift assumption for substitution-rule day-type.
 */

import {
  coverageGapsField,
  resolveRequiredAllowance,
  resolveRequiredPenalty,
  type RateContext,
  type RateLine,
  type PenaltyLine,
  type AllowanceLine,
} from '../award-rate-lookup';
import { backOutRoundingUnits, roundMoney, sumMoney } from '../money-rounding';
import type {
  ShiftPayBreakdown,
  ShiftPayInput,
  ShiftPayWithOvertimeInput,
  ShiftPayAllowanceLine,
  ShiftClassificationSnapshot,
  NormalizedSchadsLevel,
} from '../schads';
import { payrollBreakMinutesForEngine } from '../payroll/break-treatment';
import { applyDstStartOrdinaryTopUp } from '../payroll/multi-zone-pay-clock';
import {
  overtimeMealAllowanceCrossings,
  shiftClassificationGeometry,
} from '../payroll/shift-classification-geometry';
import {
  ordinaryHoursForShiftWithinConfiguredLimits,
  subtractOvertimeFromOrdinaryBands,
  ordinaryOvertimeBackoutHours,
} from '../schads';
import { classifyShift, getOvertimeSegmentBreakdown, type DayType } from './shift-classifier';
import { calculateRecallPay, type RecallPayBreakdown, type RecallPayInput } from './recall';
import type { AwardEngine, AwardCode } from './types';
import { unavailableEvaluationTrace } from './evaluation-trace';
import {
  HPSS_SHIFT_RULES,
  HPSS_RECALL_RULES,
  HPSS_STREAMS,
  HPSS_MEAL_OT_EMISSION,
  HPSS_LEAVE,
} from '../award-data/hpss';

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

const round2 = (n: number): number => roundMoney(n);
const round4 = (n: number): number => Math.round(n * 10000) / 10000;

/**
 * Phase 1A.5 + 1B: all engine-local segmentation / day-of-week helpers
 * have been removed; the engine consumes `classifyShift` +
 * `getOvertimeSegmentBreakdown` from `./shift-classifier.ts` for both
 * the base calculation and the OT-end-of-shift refinement.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Classification + rate resolution
// ═══════════════════════════════════════════════════════════════════════════

interface ResolvedRate {
  hourlyRate: number;
  level: string;
  payPoint: string | null;
  recognised: boolean;
}

function lookupRate(
  ctx: RateContext,
  stream: string,
  level: string,
  payPoint: string | null,
): RateLine | null {
  const candidates = ctx.rates.filter(
    (r) =>
      r.classificationStream === stream &&
      r.classificationLevel === level &&
      (r.payPoint ?? null) === payPoint,
  );
  return candidates[0] ?? null;
}

interface HpssInputExtras {
  classificationStream?: string;
  classificationLevel?: string;
  classificationPayPoint?: string | null;
  /**
   * Phase 1D (Award Engine Integrity 2026-05) — worker's substantive
   * classification. See nurses.ts / aged-care.ts for the shape +
   * acting/substantive resolution semantics.
   */
  substantiveClassification?: ShiftClassificationSnapshot;
  /**
   * Phase 1O (Award Engine Integrity 2026-05-17) — if true, the employer
   * provided an adequate meal during OT (cl 23.3(e)(i) provision-of-meal
   * carve-out). Suppresses BOTH MEAL_ALLOWANCE and MEAL_OT_ADDITIONAL_ALLOWANCE
   * emissions. Defaults to `undefined`/`false` (no carve-out → meals fire
   * when OT thresholds are met). Coordinator UI / mobile data-capture is
   * W22-A.1 post-Madea; until then, callers don't set this and the engine
   * emits meal allowances when the OT-duration trigger fires (W22-A
   * conditional over-pay risk documented in CLAUDE.md "Known platform gaps").
   */
  mealProvidedDuringOT?: boolean | null;
  /**
   * Phase 1O (Award Engine Integrity 2026-05-17) — rare exceptional
   * carve-out per cl 23.3(e)(iii): if true, the worker could feasibly
   * return home for the meal within the meal break, so the allowance is
   * not payable. Suppresses BOTH MEAL_ALLOWANCE and MEAL_OT_ADDITIONAL_ALLOWANCE
   * emissions. Defaults to `undefined`/`false` (no carve-out). Symmetric
   * shape with Aged Care `AgedCareInputExtras.couldReturnHomeForMeal` (W4)
   * and Nurses `NursesShiftInputExtras.couldReturnHomeForMeal` (W22-A) —
   * encoded INDEPENDENTLY per per-award isolation rule.
   */
  couldReturnHomeForMeal?: boolean | null;
}

function resolveActingVsSubstantive(
  resolvedStream: string | null,
  resolvedLevel: string,
  resolvedPayPoint: string | null,
  substantive: ShiftClassificationSnapshot | undefined,
): {
  actingClassification: ShiftClassificationSnapshot | null;
  substantiveClassification: ShiftClassificationSnapshot | null;
} {
  if (!substantive) {
    return { actingClassification: null, substantiveClassification: null };
  }
  const sameAsActing =
    substantive.classificationStream === resolvedStream &&
    substantive.classificationLevel === resolvedLevel &&
    (substantive.payPoint ?? null) === (resolvedPayPoint ?? null);
  if (sameAsActing) {
    return { actingClassification: null, substantiveClassification: null };
  }
  return {
    actingClassification: {
      classificationStream: resolvedStream,
      classificationLevel: resolvedLevel,
      payPoint: resolvedPayPoint ?? null,
    },
    substantiveClassification: {
      classificationStream: substantive.classificationStream,
      classificationLevel: substantive.classificationLevel,
      payPoint: substantive.payPoint,
    },
  };
}

function resolveHpssRate(ctx: RateContext, input: ShiftPayInput & HpssInputExtras): ResolvedRate {
  const stream = input.classificationStream ?? 'health_professional';
  const level = input.classificationLevel;
  const payPoint = input.classificationPayPoint ?? null;
  if (!level) {
    return { hourlyRate: 0, level: '', payPoint, recognised: false };
  }
  const rate = lookupRate(ctx, stream, level, payPoint);
  if (!rate) {
    return { hourlyRate: 0, level, payPoint, recognised: false };
  }
  return { hourlyRate: rate.hourlyRate, level, payPoint, recognised: true };
}

// ═══════════════════════════════════════════════════════════════════════════
// Penalty + allowance lookups (mirror Aged Care pattern; SCHADS-style inclusive casual)
// ═══════════════════════════════════════════════════════════════════════════

type EmploymentLabel = 'PERMANENT' | 'CASUAL';

function findPenalty(
  penalties: PenaltyLine[],
  band: string,
  employment: EmploymentLabel,
): number | null {
  return (
    penalties.find((p) => p.band === band && p.employmentType === employment)?.multiplier ?? null
  );
}

function findAllowance(
  allowances: AllowanceLine[],
  code: string,
  employment: EmploymentLabel | null,
): AllowanceLine | null {
  const employmentKey = employment === 'CASUAL' ? 'CASUAL' : 'PERMANENT';
  const specific = allowances.find(
    (a) => a.allowanceCode === code && a.appliesToEmployment === employmentKey,
  );
  if (specific) return specific;
  return (
    allowances.find((a) => a.allowanceCode === code && (a.appliesToEmployment ?? null) === null) ??
    null
  );
}

function resolveAllowance(
  ctx: RateContext,
  code: string,
  employment: EmploymentLabel | null,
  coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): AllowanceLine | null {
  return resolveRequiredAllowance(ctx, code, employment, {
    lookup: (allowanceCode, lookupEmployment) =>
      findAllowance(ctx.allowances, allowanceCode, lookupEmployment as EmploymentLabel | null),
    coverageGaps,
  });
}

const ZERO_BREAKDOWN = (
  level: string | null,
  baseHourlyRate: number,
  warnings: string[],
): ShiftPayBreakdown => ({
  normalizedLevel: level as NormalizedSchadsLevel | null,
  baseHourlyRate,
  hoursWorked: 0,
  bands: {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  },
  basePay: 0,
  penaltyPay: 0,
  shiftLoadingPay: 0,
  overtimeHours: 0,
  overtimePay: 0,
  overtimeFirstHours: 0,
  overtimeRemainderHours: 0,
  overtimePublicHolidayHours: 0,
  allowancesPay: 0,
  allowanceLines: [],
  grossTotal: 0,
  warnings,
});

// ═══════════════════════════════════════════════════════════════════════════
// Engine
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Exact (unrounded) ordinary pay units that calculate() rounded into basePay
 * (weekday) and penaltyPay (Saturday, Sunday, public holiday), keyed by the
 * breakdown it returned. Never serialised. calculateWithOvertime backs out
 * overtime per unit from these (Astra RND-04/RND-07), so any other money in
 * the same component is carried through exactly.
 */
const ORDINARY_PAY_UNITS = new WeakMap<
  ShiftPayBreakdown,
  { weekday: number; saturday: number; sunday: number; publicHoliday: number }
>();

function calculate(
  rawInput: ShiftPayInput & HpssInputExtras,
  ctx?: RateContext,
): ShiftPayBreakdown {
  const warnings: string[] = [];
  if (!ctx) {
    warnings.push(
      'HpssEngine called without a RateContext — Phase 1 always loads ctx from DB; this path is for dev tests only.',
    );
    return ZERO_BREAKDOWN(null, 0, warnings);
  }

  const stream = rawInput.classificationStream ?? 'health_professional';
  if (!HPSS_STREAMS.has(stream)) {
    warnings.push(
      `HpssEngine called with classification stream '${stream}' — only ${Array.from(HPSS_STREAMS).join(', ')} are supported.`,
    );
    return ZERO_BREAKDOWN(null, 0, warnings);
  }

  const tz = rawInput.timezone ?? 'Australia/Sydney';
  const resolved = resolveHpssRate(ctx, rawInput);
  if (!resolved.recognised) {
    warnings.push(
      `HPSS classification "${rawInput.classificationLevel ?? '?'}" not recognised under stream '${stream}' — gross pay will be $0.`,
    );
  }

  const baseHourlyRate = resolved.hourlyRate;
  const normalizedLevel = resolved.recognised ? (resolved.level as NormalizedSchadsLevel) : null;

  const totalMs = rawInput.endTime.getTime() - rawInput.startTime.getTime();
  if (totalMs <= 0) {
    warnings.push('Shift end must be after start — returning zero pay.');
    return ZERO_BREAKDOWN(normalizedLevel, baseHourlyRate, warnings);
  }
  const rawHours = totalMs / (1000 * 60 * 60);
  const deductibleBreakMinutes = payrollBreakMinutesForEngine(
    rawInput.breakMinutes ?? 0,
    rawInput.breakTreatment,
    rawInput.breakSemantics,
  );
  const breakHours = deductibleBreakMinutes / 60;
  const hoursWorked = Math.max(0, rawHours - breakHours);

  const employment: EmploymentLabel = rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
  const coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']> = [];

  // Phase 1A.5 (Award Engine Integrity 2026-05) — whole-shift majority +
  // finish-time character per HPSS Schedule C.1 + cl 26 (mirroring Nurses
  // cl 27.2 shape). See `shift-classifier.ts` for the algorithm and
  // architecture decision D1 (15 May 2026).
  const classificationTimes = shiftClassificationGeometry(rawInput);
  const classified = classifyShift({
    startTime: classificationTimes.startTime,
    endTime: classificationTimes.endTime,
    breakMinutes: classificationTimes.breakMinutes,
    timezone: tz,
    isPublicHoliday: rawInput.isPublicHoliday ?? false,
    publicHolidaySegments: rawInput.publicHolidaySegments,
    shiftCharacterRules: HPSS_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
    permanentNightShiftWorker: false,
  });

  const bands = {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  };

  const isShiftworker = (rawInput.employeeCategory ?? 'dayworker') === 'shiftworker';

  switch (classified.dominantDayType) {
    case 'publicHoliday':
      bands.publicHoliday = hoursWorked;
      break;
    case 'saturday':
      bands.saturdayOrdinary = hoursWorked;
      break;
    case 'sunday':
      bands.sundayOrdinary = hoursWorked;
      break;
    case 'weekday':
      bands.weekdayOrdinary = hoursWorked;
      if (isShiftworker) {
        if (classified.shiftCharacter === 'AFTERNOON') {
          bands.eveningShift = hoursWorked;
        } else if (
          classified.shiftCharacter === 'NIGHT' ||
          classified.shiftCharacter === 'PERMANENT_NIGHT'
        ) {
          bands.nightShift = hoursWorked;
        }
      }
      break;
  }

  // HPSS hybrid casual model (cl 11.4 + cl 26):
  //   - Weekday ordinary: 25% loading on base
  //   - Saturday/Sunday: stored multiplier IS the final rate; loading NOT added
  //   - PH: stored multiplier IS the final rate (already 275% for casual)
  //   - Shift loading: stored multiplier IS the final rate; loading NOT added
  // Engine implements this by paying weekdayMult on the weekday band, then
  // subtracting the casual loading amount from the shift-loading delta so
  // the shift band doesn't double-count.
  const weekdayMult = employment === 'CASUAL' ? 1.25 : 1.0;
  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);
  const satMult = resolveRequiredPenalty(ctx, 'SATURDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const sunMult = resolveRequiredPenalty(ctx, 'SUNDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const phMult = resolveRequiredPenalty(ctx, 'PUBLIC_HOLIDAY', employment, 2.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const eveningMult = resolveRequiredPenalty(ctx, 'WEEKDAY_EVENING', employment, 1.15, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const nightMult = resolveRequiredPenalty(ctx, 'WEEKDAY_NIGHT', employment, 1.15, {
    lookup: penaltyLookup,
    coverageGaps,
  });

  const ordinaryPayUnits = {
    weekday: bands.weekdayOrdinary * baseHourlyRate * weekdayMult,
    saturday: bands.saturdayOrdinary * baseHourlyRate * satMult,
    sunday: bands.sundayOrdinary * baseHourlyRate * sunMult,
    publicHoliday: bands.publicHoliday * baseHourlyRate * phMult,
  };
  const basePay = round2(ordinaryPayUnits.weekday);
  const satPay = round2(ordinaryPayUnits.saturday);
  const sunPay = round2(ordinaryPayUnits.sunday);
  const phPay = round2(ordinaryPayUnits.publicHoliday);
  const penaltyPay = round2(satPay + sunPay + phPay);
  // Hybrid shift extra: subtract casual loading so shift+ordinary doesn't compound
  // For permanent: weekdayMult=1.0, eveningMult=1.15 → extra = 1.15-1=0.15 ✓ (paid 1.15× total)
  // For casual:    weekdayMult=1.25, eveningMult=1.40 → extra = 1.40-1.25=0.15 ✓ (paid 1.40× total)
  const eveningExtra = round2(bands.eveningShift * baseHourlyRate * (eveningMult - weekdayMult));
  const nightExtra = round2(bands.nightShift * baseHourlyRate * (nightMult - weekdayMult));
  const shiftLoadingPay = round2(eveningExtra + nightExtra);

  // Allowances
  const allowanceLines: ShiftPayAllowanceLine[] = [];
  let allowancesPay = 0;

  function emit(code: string, label: string, amount: number, clauseRef: string | null = null) {
    if (amount <= 0) return;
    allowancesPay = round2(allowancesPay + amount);
    allowanceLines.push({ code, description: label, amount, clauseRef });
  }

  // Phase 1N (2026-05-17) — canonical emission codes per
  // `project_allowance_code_naming_taxonomy.md`. DB lookup keys unchanged;
  // emitted line `code` field uses canonical _ALLOWANCE-suffixed names.
  if ((rawInput.travelKm ?? 0) > 0) {
    const km = resolveAllowance(ctx, 'KM', employment, coverageGaps);
    if (km)
      emit(
        'KM_ALLOWANCE',
        km.description ?? 'Travel km',
        round2((rawInput.travelKm ?? 0) * km.amount),
        km.clauseRef ?? null,
      );
  }
  if (rawInput.hasUniformAllowance) {
    const uniform = resolveAllowance(ctx, 'UNIFORM_PER_SHIFT', employment, coverageGaps);
    if (uniform)
      emit(
        'UNIFORM_ALLOWANCE',
        uniform.description ?? 'Uniform',
        uniform.amount,
        uniform.clauseRef ?? null,
      );
  }
  if (rawInput.hasLaundryAllowance) {
    const laundry = resolveAllowance(ctx, 'LAUNDRY_PER_SHIFT', employment, coverageGaps);
    if (laundry)
      emit(
        'LAUNDRY_ALLOWANCE',
        laundry.description ?? 'Laundry',
        laundry.amount,
        laundry.clauseRef ?? null,
      );
  }
  // Phase 1I.4 (Award Engine Integrity 2026-05-16) — first-aid allowance
  // is DELIBERATELY NOT EMITTED for MA000027. The shared
  // `hasFirstAidResponsibility` input flag is consumed by SCHADS, Nurses
  // and Aged Care engines (which have first-aid clauses) but MA000027 has
  // no first-aid allowance — Phase 1I.0 sweep B.5 verified absence against
  // the full FWC consolidated MA000027 allowance schedule. The prior
  // emission path was a no-op in production (no FIRST_AID seed row exists
  // under MA000027 active rate versions, verified live DB 2026-05-16) but
  // is removed to prevent future seed-pollution from silently activating
  // an over-award entitlement.

  // Phase 1I.4 — HPSS on-call allowance per MA000027 cl 23.2(d).
  // Per-24h-period flat: cl 23.2(d)(i) $25.15 Mon–Sat ("Monday and Saturday
  // INCLUSIVE" — Saturday on-call pays at the cl (d)(i) rate, not the
  // cl (d)(ii) rate); cl 23.2(d)(ii) $50.18 Sun + PH. Allowance codes
  // ON_CALL_WEEKDAY and ON_CALL_WEEKEND are RETAINED to preserve seed
  // compatibility — they are semantically misnamed (the codes mean
  // "MON_SAT" and "SUN_PH" respectively per cl 23.2(d)) but renaming the
  // codes would require a DB seed migration without engine-math benefit.
  //
  // HPSS cl 23.2(d) is a FLAT per-24h-period allowance. Phase 1F's Nurses
  // majority-of-shift allocation rule (MA000034 cl 19.3 read with FWO
  // library K700647) does NOT apply to HPSS — see
  // docs/operations/clause-verifications/2026-05-16-aged-care-legislation-hpss-tier-2-3-sweep.md
  // section A.3. The 24h-period is whole-or-nothing per the award's "or part
  // thereof" phrasing; callers are expected to pass integer day counts.
  if ((rawInput.onCallWeekdayDays ?? 0) > 0) {
    const oc = resolveAllowance(ctx, 'ON_CALL_WEEKDAY', employment, coverageGaps);
    if (oc)
      emit(
        'ON_CALL_WEEKDAY_ALLOWANCE',
        oc.description ?? 'On-call Mon–Sat (cl 23.2(d)(i))',
        round2((rawInput.onCallWeekdayDays ?? 0) * oc.amount),
        oc.clauseRef ?? null,
      );
  }
  if ((rawInput.onCallWeekendDays ?? 0) > 0) {
    const oc = resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps);
    if (oc)
      emit(
        'ON_CALL_WEEKEND_ALLOWANCE',
        oc.description ?? 'On-call Sun + PH (cl 23.2(d)(ii))',
        round2((rawInput.onCallWeekendDays ?? 0) * oc.amount),
        oc.clauseRef ?? null,
      );
  }
  // Meal allowance (cl 23.3(e)) — Phase 1O (Award Engine Integrity
  // 2026-05-17) MOVED OUT of OT-unaware calculate() and INTO OT-aware
  // calculateWithOvertime() so emission triggers on the OT-duration
  // threshold (cl 23.3(e)(i): OT > 1h; cl 23.3(e)(ii): OT > 4h for further
  // meal) rather than a caller `hasDelayedMealAllowance` boolean. Mirrors
  // the W4 Aged Care implementation. See HPSS_MEAL_OT_EMISSION in
  // award-data/hpss.ts for the SSOT constant. Closes W3 KNOWN GAP +
  // W22/W22-B P0 wage-theft criminal regime exposure.

  // Annual leave loading under MA000027 cl 27.3. The engine emits the flat
  // component for non-casual workers; the pay-run leave snapshot applies the
  // shiftworker higher-of comparison from roster evidence.
  if (rawInput.segmentKind === 'paid_annual_leave' && rawInput.employmentType !== 'casual') {
    const ordinaryLeavePay = round2(hoursWorked * baseHourlyRate);
    const loadingAmount = round2(ordinaryLeavePay * HPSS_LEAVE.annualLeaveLoading);
    emit(
      'LEAVE_LOADING_ALLOWANCE',
      'Annual leave loading (17.5%)',
      loadingAmount,
      HPSS_LEAVE.clauseRef,
    );
  }

  const grossTotal = sumMoney(basePay, penaltyPay, shiftLoadingPay, allowancesPay);

  const result: ShiftPayBreakdown = {
    normalizedLevel,
    baseHourlyRate,
    hoursWorked: round4(hoursWorked),
    bands: {
      weekdayOrdinary: round4(bands.weekdayOrdinary),
      saturdayOrdinary: round4(bands.saturdayOrdinary),
      sundayOrdinary: round4(bands.sundayOrdinary),
      publicHoliday: round4(bands.publicHoliday),
      eveningShift: round4(bands.eveningShift),
      nightShift: round4(bands.nightShift),
    },
    basePay,
    penaltyPay,
    shiftLoadingPay,
    overtimeHours: 0,
    overtimePay: 0,
    overtimeFirstHours: 0,
    overtimeRemainderHours: 0,
    overtimePublicHolidayHours: 0,
    allowancesPay,
    allowanceLines,
    grossTotal,
    warnings,
    ...coverageGapsField(coverageGaps),
    ...resolveActingVsSubstantive(
      stream,
      resolved.level,
      resolved.payPoint,
      rawInput.substantiveClassification,
    ),
  };
  ORDINARY_PAY_UNITS.set(result, ordinaryPayUnits);
  return result;
}

function calculateWithOvertime(
  rawInput: ShiftPayWithOvertimeInput & HpssInputExtras,
  ctx?: RateContext,
): ShiftPayBreakdown {
  // Phase 1I.2 (Award Engine Integrity 2026-05-16) — HPSS OT thresholds per
  // MA000027 cl 13.1 + cl 25.1:
  //   - Weekly ordinary cap: 38h (cl 13.1(a) + cl 25.1).
  //   - Daily ordinary cap: 10h (cl 13.1(b)). Above 10h: tier-1 OT 1.5× for
  //     first 2h, tier-2 OT 2.0× thereafter (cl 25.2(a)(i)). PH OT 2.5×
  //     (cl 25.2(a)(iii)); Sunday OT 2.0× (cl 25.2(a)(ii)).
  // Corrects Phase 1E/1F default of 8h/day which the Phase 1I.0 sweep
  // initially attributed to cl 21.2(b). cl 21 is "Payment of wages"; the
  // actual ordinary-hours provision is cl 13. See
  // `docs/operations/clause-verifications/2026-05-16-aged-care-legislation-hpss-daily-ot-threshold.md`.
  const baseBreakdown = calculate(rawInput, ctx);
  if (!ctx || baseBreakdown.baseHourlyRate === 0) return baseBreakdown;

  const totalShiftHours = baseBreakdown.hoursWorked;
  const ordinaryHoursForShift = ordinaryHoursForShiftWithinConfiguredLimits(
    rawInput,
    totalShiftHours,
    10,
  );
  const overtimeHours = Math.max(0, totalShiftHours - ordinaryHoursForShift);
  if (overtimeHours <= 0) return baseBreakdown;

  // Phase 1B (Award Engine Integrity 2026-05) — per-day-type OT split using
  // getOvertimeSegmentBreakdown (HPSS Schedule C OT + SCHADS-style
  // inclusive casual). Mirrors the Aged Care implementation; see
  // nurses.ts for the rationale and shift-classifier.ts §1.2.
  const tz = rawInput.timezone ?? 'Australia/Sydney';
  const isPublicHoliday = rawInput.isPublicHoliday ?? false;
  const baseHourlyRate = baseBreakdown.baseHourlyRate;
  const employment: EmploymentLabel = rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
  const casualMult = employment === 'CASUAL' ? 1.25 : 1.0;
  const coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']> = [
    ...(baseBreakdown.coverageGaps ?? []),
  ];
  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);
  const satMult = resolveRequiredPenalty(ctx, 'SATURDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const sunMult = resolveRequiredPenalty(ctx, 'SUNDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const phMult = resolveRequiredPenalty(ctx, 'PUBLIC_HOLIDAY', employment, 2.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });

  const classificationTimes = shiftClassificationGeometry(rawInput);
  const classifiedForOT = classifyShift({
    startTime: classificationTimes.startTime,
    endTime: classificationTimes.endTime,
    breakMinutes: classificationTimes.breakMinutes,
    timezone: tz,
    isPublicHoliday,
    publicHolidaySegments: rawInput.publicHolidaySegments,
    shiftCharacterRules: HPSS_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
    permanentNightShiftWorker: false,
  });
  const otBreakdown = getOvertimeSegmentBreakdown(classifiedForOT, overtimeHours);
  const otByDay = otBreakdown.hoursByDayType;

  let overtimePay = 0;
  let tier1Remaining = Math.max(
    0,
    2 - Math.max(0, rawInput.overtimeInitialTierHoursConsumedEarlierThisShift ?? 0),
  );

  if (otByDay.publicHoliday > 0) {
    overtimePay += otByDay.publicHoliday * baseHourlyRate * 2.5 * casualMult;
  }
  if (otByDay.sunday > 0) {
    overtimePay += otByDay.sunday * baseHourlyRate * 2.0 * casualMult;
  }
  const weekdaySat: DayType[] = ['saturday', 'weekday'];
  for (const dt of weekdaySat) {
    const hoursInDay = otByDay[dt];
    if (hoursInDay <= 0) continue;
    const t1 = Math.min(tier1Remaining, hoursInDay);
    const t2 = hoursInDay - t1;
    overtimePay += (t1 * baseHourlyRate * 1.5 + t2 * baseHourlyRate * 2.0) * casualMult;
    tier1Remaining -= t1;
  }
  overtimePay = round2(overtimePay);

  // Substitution rule: per-day-type back-out of ordinary-rate dollars.
  const ordinaryOtByDay = ordinaryOvertimeBackoutHours(
    baseBreakdown.bands,
    otByDay,
    rawInput.publicHolidaySegments != null,
  );
  let adjustedBasePay = baseBreakdown.basePay;
  let adjustedPenaltyPay = baseBreakdown.penaltyPay;
  // Per-unit back-out (RND-04/07): each band calculate() rounded on its own is
  // replaced by the once-rounded remainder; other money stays untouched.
  const ordinaryPayUnits = ORDINARY_PAY_UNITS.get(baseBreakdown);
  if (ordinaryOtByDay.weekday > 0) {
    adjustedBasePay = ordinaryPayUnits
      ? backOutRoundingUnits(adjustedBasePay, [
          {
            exact: ordinaryPayUnits.weekday,
            removal: ordinaryOtByDay.weekday * baseHourlyRate * casualMult,
          },
        ])
      : round2(
          Math.max(0, adjustedBasePay - ordinaryOtByDay.weekday * baseHourlyRate * casualMult),
        );
  }
  const directPenaltyBackout =
    ordinaryPayUnits &&
    (ordinaryOtByDay.saturday > 0 ||
      ordinaryOtByDay.sunday > 0 ||
      ordinaryOtByDay.publicHoliday > 0)
      ? backOutRoundingUnits(adjustedPenaltyPay, [
          {
            exact: ordinaryPayUnits.saturday,
            removal: ordinaryOtByDay.saturday * baseHourlyRate * satMult * casualMult,
          },
          {
            exact: ordinaryPayUnits.sunday,
            removal: ordinaryOtByDay.sunday * baseHourlyRate * sunMult * casualMult,
          },
          {
            exact: ordinaryPayUnits.publicHoliday,
            removal: ordinaryOtByDay.publicHoliday * baseHourlyRate * phMult * casualMult,
          },
        ])
      : null;
  if (directPenaltyBackout !== null) {
    adjustedPenaltyPay = directPenaltyBackout;
  } else if (ordinaryOtByDay.saturday > 0) {
    adjustedPenaltyPay = round2(
      Math.max(
        0,
        adjustedPenaltyPay - ordinaryOtByDay.saturday * baseHourlyRate * satMult * casualMult,
      ),
    );
  }
  if (directPenaltyBackout === null && ordinaryOtByDay.sunday > 0) {
    adjustedPenaltyPay = round2(
      Math.max(
        0,
        adjustedPenaltyPay - ordinaryOtByDay.sunday * baseHourlyRate * sunMult * casualMult,
      ),
    );
  }
  if (directPenaltyBackout === null && ordinaryOtByDay.publicHoliday > 0) {
    adjustedPenaltyPay = round2(
      Math.max(
        0,
        adjustedPenaltyPay - ordinaryOtByDay.publicHoliday * baseHourlyRate * phMult * casualMult,
      ),
    );
  }

  // Phase 1O (Award Engine Integrity 2026-05-17) — Meal-OT emission per
  // MA000027 cl 23.3(e)(i) + cl 23.3(e)(ii). First meal $16.62 when OT > 1h
  // past usual finish; second meal $14.98 additional when OT > 4h cumulative.
  // CUMULATIVE semantics (worker doing 5h OT gets $16.62 + $14.98). NOT
  // all-purpose — flat emission, casual loading does NOT compound. Carve-outs:
  // provision-of-meal (cl 23.3(e)(i)) suppresses both; rare could-return-
  // home-for-meal (cl 23.3(e)(iii)) also suppresses. See HPSS_MEAL_OT_EMISSION
  // in award-data/hpss.ts. Mirror of W4 Aged Care implementation.
  // Resolve employment for findAllowance lookup — base breakdown was built
  // by calculate() but employment context wasn't propagated; derive same way.
  const employmentForMeal: 'PERMANENT' | 'CASUAL' =
    rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
  const allowanceLinesOut: ShiftPayAllowanceLine[] = [...baseBreakdown.allowanceLines];
  let allowancesPayOut = baseBreakdown.allowancesPay;
  const mealCarveOut =
    (rawInput.mealProvidedDuringOT ?? false) || (rawInput.couldReturnHomeForMeal ?? false);
  const mealCrossings = overtimeMealAllowanceCrossings({
    fragmentOvertimeHours: overtimeHours,
    priorOvertimeHoursThisShift: rawInput.overtimeHoursWorkedEarlierThisShift,
    firstMealThresholdHours: HPSS_MEAL_OT_EMISSION.firstMealThresholdHours,
    secondMealThresholdHours: HPSS_MEAL_OT_EMISSION.secondMealThresholdHours,
  });
  if (mealCrossings.emitFirstMeal && !mealCarveOut) {
    const firstMeal = resolveAllowance(
      ctx,
      HPSS_MEAL_OT_EMISSION.firstMealAllowanceCode,
      employmentForMeal,
      coverageGaps,
    );
    if (firstMeal) {
      allowancesPayOut = round2(allowancesPayOut + firstMeal.amount);
      allowanceLinesOut.push({
        code: 'MEAL_ALLOWANCE',
        description: firstMeal.description ?? 'Meal allowance (overtime)',
        amount: firstMeal.amount,
        clauseRef: firstMeal.clauseRef ?? HPSS_MEAL_OT_EMISSION.clauseRefFirst,
      });
    }
    if (mealCrossings.emitSecondMeal) {
      const secondMeal = resolveAllowance(
        ctx,
        HPSS_MEAL_OT_EMISSION.secondMealAllowanceCode,
        employmentForMeal,
        coverageGaps,
      );
      if (secondMeal) {
        allowancesPayOut = round2(allowancesPayOut + secondMeal.amount);
        allowanceLinesOut.push({
          code: 'MEAL_OT_ADDITIONAL_ALLOWANCE',
          description: secondMeal.description ?? 'Additional meal allowance (further OT > 4h)',
          amount: secondMeal.amount,
          clauseRef: secondMeal.clauseRef ?? HPSS_MEAL_OT_EMISSION.clauseRefSecond,
        });
      }
    }
  }

  const grossTotal = sumMoney(
    adjustedBasePay,
    adjustedPenaltyPay,
    baseBreakdown.shiftLoadingPay,
    overtimePay,
    allowancesPayOut,
  );

  return {
    ...baseBreakdown,
    bands: subtractOvertimeFromOrdinaryBands(baseBreakdown.bands, ordinaryOtByDay),
    basePay: adjustedBasePay,
    penaltyPay: adjustedPenaltyPay,
    allowancesPay: allowancesPayOut,
    allowanceLines: allowanceLinesOut,
    overtimeHours: round4(overtimeHours),
    overtimePay,
    grossTotal,
    ...coverageGapsField(coverageGaps),
  };
}

/**
 * Semver of the HPSS Award (MA000027) engine. Bumped manually on every
 * math-affecting change to this file. Persisted to
 * `pay_run_items.snapshot_engine_version` via `finalise_pay_run_snapshot`,
 * so each row is byte-reproducible against the engine code that produced it.
 *
 * Enforced by `.husky/pre-commit` + CI assertion: any modification to this
 * file (other than the version line itself) requires this string to change,
 * unless the commit message contains `[engine-no-math-change]`.
 */
// Phase 1N (2026-05-17) — bumped 5.5.6 → 5.6.0 (semver MINOR). Output contract
// change per `project_allowance_code_naming_taxonomy.md`. Emission codes use
// canonical _ALLOWANCE-suffixed names (KM → KM_ALLOWANCE; ON_CALL_WEEKDAY →
// ON_CALL_WEEKDAY_ALLOWANCE; MEAL → MEAL_ALLOWANCE; UNIFORM_PER_SHIFT →
// UNIFORM_ALLOWANCE; etc.). Engine math UNCHANGED. DB lookup keys unchanged.
//
// Phase 1O (2026-05-17) — bumped 5.6.0 → 5.6.1 (semver PATCH; math change).
// Meal-OT split (cl 23.3(e)(i) + (ii)) + return-home carveout (cl 23.3(e)(iii))
// per W22 evidence pack. Closes the W3 KNOWN GAP listed at the head of
// award-data/hpss.ts ("cl 23.3(e)(ii) MEAL_OT_ADDITIONAL second-meal
// emission path"). Emission moved from OT-unaware calculate() (where it
// fired on `hasDelayedMealAllowance` boolean — silently dropping the
// $14.98 further-meal allowance for every OT > 4h shift) to OT-aware
// calculateWithOvertime() where it triggers on the cl 23.3(e)(i)+(ii)
// OT-duration thresholds. Carve-out flags `mealProvidedDuringOT` +
// `couldReturnHomeForMeal` suppress both emissions per cl 23.3(e)(iii).
// W22-B P0 wage-theft criminal regime exposure CLOSED. Mirror of W4 Aged
// Care shape per cross-award native symmetry binding (encoded independently
// per per-award isolation rule — see HPSS_MEAL_OT_EMISSION in award-data/hpss.ts).
//
// Phase 3 PR #3 sub-phase 3G (2026-05-18) — bumped 5.6.1 → 5.6.2 (semver PATCH).
// New LEAVE_LOADING_ALLOWANCE emission per cl 27.3(a) gated on
// segmentKind === 'paid_annual_leave' AND non-casual employmentType. Flat
// 17.5% × ordinary leave pay; cl 27.3(b) higher-of is applied by the
// pay-run leave-snapshot layer.
// All pre-3G callers default segmentKind='work' → byte-equal for work
// segments. T2 sweep + Path-A pins cover the new emission path.
//
// HPSS annual leave loading clause reference verified against the FWC
// consolidated MA000027 current to 1 July 2026: cl 27.3(a) and (b).
//
// Phase Codex Pass A (2026-05-19) — bumped 5.6.2 → 5.7.0 (semver MINOR).
// MA000027 cl 26.3 OR-triggered single-band shift loading rebuilt. Pre-
// Phase finish-only Nurses-shape port `{ afternoon: { finish: 18-24 },
// night: { finish: 0-8 } }` mis-classified 18:00-08:30 (commences in
// 18-06 window BUT finishes 08:30 outside 18-08 window) as DAY (no
// loading). Correct cl 26.3: commence-in-window OR finish-in-window
// triggers 115% single band. Direction: UNDER-pay corrected UP. Engine
// math CHANGED for 18:00-start late-finish shifts; byte-equal preserved
// for all other patterns (the new OR-shape is a strict SUPERSET of the
// pre-Phase finish-only firing surface for the existing seeded 1.15
// multiplier).
// Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
//
// Phase Codex Pass A PR #B (2026-05-19) — bumped 5.7.0 → 5.7.1 (semver PATCH).
// [engine-math-changed] tag — `calculateRecall` math materially changed
// per FWC cl 25.2 + cl 25.3 + cl 25.6 primary-source verification. Pre-fix
// `HpssEngine.calculateRecall` collapsed all paid hours to a single flat
// 1.5× tier-1 multiplier; post-fix applies banded OT for Mon-Sat (first
// 2h @ 150% / casual 187.5%, after 2h @ 200% / casual 250% per cl 25.2
// FT/PT + cl 25.3 casual), FLAT 200% / casual 250% for Sunday, FLAT 250%
// / casual 312.5% for PH. NOTE: original Codex brief cited cl 28.2 which
// is WRONG (cl 28 is "Personal/carer's leave"); correct clauses are
// 25.2 / 25.3 / 25.6 per specialist artifact §3. `calculate` +
// `calculateWithOvertime` (timesheet path) are UNCHANGED — only recall
// pay is affected. Artifact:
// docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
//
// EA ordinary-hours cycle bridge (2026-06-09) — bumped 5.7.1 → 5.7.2
// (semver PATCH). `calculateWithOvertime` now accepts configured ordinary
// cycle caps and returns ordinary-hour bands with OT-tail hours removed, so
// pay-run hour columns and cumulative ordinary tracking match OT substitution.
// Legacy callers without cycle inputs remain byte-equal for pay dollars.
//
// EBA Phase 2 strict-mode (2026-07-03) — bumped 5.7.3 → 5.7.5. The HPSS
// engine honors explicit requiredBands supplied in ctx, but the EBA block
// compiler only wires automated penalty overlays for MA000018/MA000034 in
// Phase 2; MA000027 overlays without a compile profile now block structurally.
// `calculateWithOvertime` also backs out OT-substituted Sat/Sun/PH ordinary
// dollars using resolved EBA penalty multipliers. Non-EBA output is byte-equal
// by the Phase 2 golden.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.7.5 → 5.7.6. Required EBA
// allowance rows now fail loud through coverageGaps when a triggered HPSS
// allowance lookup is missing. Non-EBA output is byte-equal.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.7.6 → 5.7.7. Recall penalty
// multipliers now fail loud through coverageGaps when a required EBA penalty
// row is missing.
//
// Medea payroll readiness (2026-07-06) — bumped 5.7.7 → 5.7.8. Shared
// OT band substitution now removes weekday loading overlays and ordinary-hour
// replay no longer double-counts loading overlays.
//
// Concurrent ordinary-hours windows (2026-07-14) — bumped 5.7.8 → 5.7.9.
// Effective provider-configured cap windows now constrain shared cumulative
// ordinary-hour headroom. Legacy callers without the new input remain byte-equal.
// MZ-004 D5 (2026-10-05) — 5.7.12 → 5.7.13. DST-start ordinary top-up line for
// permanent work segments that cross a forward transition; all else byte-equal.
// S0-05 Round 2: both-clock calendar spans feed the shared classifier.
// Half-cent rounding (2026-10-08) — 5.7.14 → 5.7.15. round2 now uses the shared
// exact half-up roundMoney (money-rounding.ts); exact half-cent amounts round
// up instead of down. No clause, rate or multiplier change.
// Overtime back-out rounds once (2026-10-08, Astra RND-04) — 5.7.15 → 5.7.16. The
// remaining ordinary/penalty dollars of hours that became overtime are priced
// once per engine rounding unit (backOutRoundingUnits) instead of
// round(round(total) − back-out), which drifted ±1c. No clause, rate or
// multiplier change.
// Overtime back-out per rounding unit (2026-10-08, Astra RND-07) — 5.7.16 → 5.7.17.
// calculate() records its exact per-band ordinary pay units; the back-out
// replaces only the units that lose hours (backOutRoundingUnits), so other
// separately rounded money in the component (e.g. a missed-meal penalty) is
// never re-rounded away. No clause, rate or multiplier change.
// ENG1 Round 7: attribute the shared trace period-boundary calculation change.
export const ENGINE_VERSION = '5.7.19';

export class HpssEngine implements AwardEngine {
  readonly awardCode: AwardCode = 'MA000027';
  readonly engineVersion: string = ENGINE_VERSION;
  readonly engineRevisionSha: string = process.env.STATURA_ENGINE_REVISION ?? 'dev';
  buildEvaluationTrace() {
    return unavailableEvaluationTrace('HPSS_PER_BAND_TRACE_NOT_YET_RETAINED');
  }

  calculate(input: ShiftPayInput, ctx?: RateContext): ShiftPayBreakdown {
    return applyDstStartOrdinaryTopUp(
      calculate(input as ShiftPayInput & HpssInputExtras, ctx),
      input,
      this.awardCode,
    );
  }
  calculateWithOvertime(input: ShiftPayWithOvertimeInput, ctx?: RateContext): ShiftPayBreakdown {
    return applyDstStartOrdinaryTopUp(
      calculateWithOvertime(input as ShiftPayWithOvertimeInput & HpssInputExtras, ctx),
      input,
      this.awardCode,
    );
  }

  calculateRecall(
    input: RecallPayInput,
    ctx: RateContext,
    baseHourlyRate: number,
    employmentType: 'PERMANENT' | 'CASUAL',
  ): RecallPayBreakdown {
    return calculateRecallPay(input, HPSS_RECALL_RULES, ctx, baseHourlyRate, employmentType);
  }
}

export const hpssEngine = new HpssEngine();
